import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { Footer } from "@/components/Footer";
import { Masthead } from "@/components/Masthead";
import { PlayerProfileEditor } from "@/components/PlayerProfileEditor";
import { Stat } from "@/components/Stat";
import { accountsApi, type Profile as ProfileData } from "@/lib/accountsApi";
import { api } from "@/lib/api";
import { formatInteger } from "@/lib/format";
import { playersApi, type PlayerState } from "@/lib/playersApi";
import { ApiError } from "@/lib/httpClient";
import { useAccount } from "@/lib/useAccount";
import { useApi } from "@/lib/useApi";

/** The page chrome, so each state below can simply return its own content. */
function ProfilePage({ children }: { readonly children: ReactNode }) {
  const club = useApi((signal) => api.club(signal));
  return (
    <>
      <Masthead variant="page" club={club.data} />
      <main className="lattice min-h-[calc(100vh-64px)] border-b-2 border-ink">
        <div className="shell py-8 md:py-10">{children}</div>
      </main>
      <Footer club={club.data} />
    </>
  );
}

/**
 * "Solved" has three states, and the table has to show all three.
 *
 * `solved` is `boolean | null`, and the rating form goes out of its way to let
 * a member answer "Did not" as distinct from leaving it blank — the club's
 * solved rate is an average that skips nulls, so the two are not the same
 * claim. Rendering `solved ? "Yes" : "—"` collapsed them back together, and a
 * member who had answered "Did not" could not see, let alone correct, what they
 * had actually submitted.
 */
function solvedLabel(solved: boolean | null): string {
  if (solved === null) return "—";
  return solved ? "Yes" : "No";
}

function RatingsTable({ rated }: { readonly rated: ProfileData["rated"] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[44rem] border-2 border-ink bg-paper">
        <thead>
          <tr className="border-b-2 border-ink text-left">
            <th className="meta px-4 py-2.5">Puzzle</th>
            <th className="meta px-4 py-2.5">You</th>
            <th className="meta px-4 py-2.5">Creator</th>
            <th className="meta px-4 py-2.5">Club</th>
            <th className="meta px-4 py-2.5">Quality</th>
            <th className="meta px-4 py-2.5">Kind</th>
            <th className="meta px-4 py-2.5">Solved</th>
          </tr>
        </thead>
        <tbody>
          {rated.map((entry) => (
            <tr
              key={entry.id}
              className="border-b-2 border-ink/15 last:border-0"
            >
              <td className="px-4 py-2.5">
                <span className="meta mr-2 font-mono opacity-50">
                  #{String(entry.id).padStart(3, "0")}
                </span>
                {entry.title}
              </td>
              <td className="px-4 py-2.5 font-mono font-bold">
                {entry.difficulty}
              </td>
              <td className="px-4 py-2.5 font-mono opacity-70">
                {formatInteger(entry.creator_difficulty)}
              </td>
              <td className="px-4 py-2.5 font-mono opacity-70">
                {entry.club_difficulty ?? "—"}
              </td>
              <td className="px-4 py-2.5 font-mono">{entry.quality ?? "—"}</td>
              <td className="px-4 py-2.5">{entry.character ?? "—"}</td>
              <td className="px-4 py-2.5">{solvedLabel(entry.solved)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The signed-in member's own editable profile.
 *
 * Its own component so a `key` on the identity can reset it. The parent used to
 * mirror the response into state and clear it with an effect, because without
 * that the previous member's text survived a sign-out-then-in and could be saved
 * into the new account. Remounting the hook starts `data` at null — the same
 * guarantee with none of the bookkeeping — and `useApi` keeps the previous value
 * across an ordinary refetch, so the form still does not blink out after a save.
 */
function MyPlayerProfile() {
  const [version, setVersion] = useState(0);
  const player = useApi<PlayerState | null>(
    (signal) => playersApi.mine(signal),
    [version],
  );

  if (player.data) {
    return (
      <PlayerProfileEditor
        state={player.data}
        onSaved={() => setVersion((current) => current + 1)}
      />
    );
  }
  if (player.loading) {
    return <p className="meta opacity-60">Loading your player profile…</p>;
  }
  // `useApi` nulls `data` on failure, so without this the whole editor simply
  // disappeared — including after a save whose refetch failed, which took the
  // success banner with it and explained nothing.
  if (player.error) {
    return (
      <p role="alert" className="panel max-w-[52ch] px-5 py-4 leading-relaxed">
        {player.error.message}
      </p>
    );
  }
  return null;
}

export function Profile() {
  const { session, loading, clearSession } = useAccount();
  // Held until the session resolves, so a signed-in visitor does not fetch
  // their profile once as a stranger and again as themselves.
  const profile = useApi<ProfileData | null>(
    (signal) =>
      loading || !session ? Promise.resolve(null) : accountsApi.profile(signal),
    [session?.user_id ?? null, loading],
  );

  const userId = session?.user_id ?? null;

  // A member session lasts thirty days, so arriving here with one the server
  // has already forgotten is ordinary. Without this the page kept the username
  // in its heading and offered the officer's admin link while every request
  // behind it failed; now it falls through to the signed-out state below.
  const expired =
    profile.error instanceof ApiError && profile.error.isUnauthenticated;
  useEffect(() => {
    if (expired) clearSession();
  }, [expired, clearSession]);

  if (loading) {
    return (
      <ProfilePage>
        <p className="meta opacity-60">…</p>
      </ProfilePage>
    );
  }

  if (!session) {
    return (
      <ProfilePage>
        <h1 className="mb-4 text-[clamp(2rem,5vw,3.25rem)]">Your profile</h1>
        <p className="panel max-w-[52ch] px-5 py-4 leading-relaxed">
          You are not signed in. Open the{" "}
          <Link to="/puzzles" className="underline underline-offset-4">
            puzzle archive
          </Link>{" "}
          to sign in or make an account.
        </p>
      </ProfilePage>
    );
  }

  const heading = (
    <div className="mb-8 flex flex-wrap items-baseline gap-x-5 gap-y-2">
      <h1 className="text-[clamp(2rem,5vw,3.25rem)] normal-case">
        {session.username}
      </h1>
      {session.is_officer ? (
        <span className="meta border-2 border-ink bg-piece-t px-2 py-1 text-[0.625rem]">
          Officer
        </span>
      ) : null}
      {profile.data ? (
        <span className="meta opacity-60">
          Member since{" "}
          {new Date(profile.data.member_since).toLocaleDateString()}
        </span>
      ) : null}
    </div>
  );

  if (profile.loading) {
    return (
      <ProfilePage>
        {heading}
        <p className="meta opacity-60">Loading your ratings…</p>
      </ProfilePage>
    );
  }

  const data = profile.data;
  if (profile.error || !data) {
    return (
      <ProfilePage>
        {heading}
        <p role="alert" className="panel px-5 py-4 leading-relaxed">
          Your profile could not be loaded. Reload the page to try again.
        </p>
      </ProfilePage>
    );
  }

  return (
    <ProfilePage>
      {heading}

      <div className="mb-10 grid gap-4 sm:grid-cols-3">
        <Stat
          label="Puzzles rated"
          value={String(data.rated_count)}
          size="panel"
        />
        <Stat label="Solved" value={String(data.solved_count)} size="panel" />
        <Stat
          size="panel"
          label="Average difficulty given"
          value={
            data.average_difficulty_given !== null
              ? String(data.average_difficulty_given)
              : "—"
          }
        />
      </div>

      <div className="mb-12">
        {/* Rendered from the *last good* state, not gated on `loading`.
            `useApi` flips loading back to true on every refetch, so keying the
            editor off it unmounted the whole form after each save — taking the
            success banner and any half-typed field with it. */}
        <MyPlayerProfile key={userId ?? "none"} />
      </div>

      <h2 className="mb-2 text-2xl">Your ratings</h2>
      <p className="mb-4 max-w-[58ch] text-[0.9375rem] leading-relaxed opacity-70">
        Your score beside the creator&rsquo;s original number and the club
        average, so you can see where you disagree.
      </p>

      {data.rated.length === 0 ? (
        <p className="panel max-w-[52ch] px-5 py-4 leading-relaxed">
          You have not rated anything yet.{" "}
          <Link to="/puzzles" className="underline underline-offset-4">
            Open the archive
          </Link>{" "}
          and settle how hard something really is.
        </p>
      ) : (
        <RatingsTable rated={data.rated} />
      )}

      {session.is_officer ? (
        <p className="panel mt-10 max-w-[58ch] px-5 py-4 leading-relaxed">
          You are signed in as an officer, so you can also{" "}
          <Link to="/admin" className="underline underline-offset-4">
            edit club content
          </Link>
          . Your password is managed there.
        </p>
      ) : null}
    </ProfilePage>
  );
}
